import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/ClientList';
import ClientIntake from './pages/ClientIntake';
import ClientDetail from './pages/ClientDetail';
import DocumentList from './pages/DocumentList';
import DocumentDetail from './pages/DocumentDetail';
import TemplateManagement from './pages/TemplateManagement';
import AdminUsers from './pages/AdminUsers';
import UserSettings from './pages/UserSettings';
import SecurityLogs from './pages/SecurityLogs';
import ActivityLogs from './pages/ActivityLogs';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/500.css';
import '@fontsource/playfair-display/600.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import './App.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="animate-pulse">
                    <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="animate-pulse">
                    <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }
    
    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }
    
    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <LoginPage />
                </PublicRoute>
            } />
            
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Dashboard />
                </ProtectedRoute>
            } />
            
            <Route path="/clients" element={
                <ProtectedRoute>
                    <ClientList />
                </ProtectedRoute>
            } />
            
            <Route path="/clients/new" element={
                <ProtectedRoute>
                    <ClientIntake />
                </ProtectedRoute>
            } />
            
            <Route path="/clients/:id" element={
                <ProtectedRoute>
                    <ClientDetail />
                </ProtectedRoute>
            } />
            
            <Route path="/clients/:id/edit" element={
                <ProtectedRoute>
                    <ClientIntake />
                </ProtectedRoute>
            } />
            
            <Route path="/documents" element={
                <ProtectedRoute>
                    <DocumentList />
                </ProtectedRoute>
            } />
            
            <Route path="/documents/:id" element={
                <ProtectedRoute>
                    <DocumentDetail />
                </ProtectedRoute>
            } />
            
            <Route path="/templates" element={
                <ProtectedRoute>
                    <TemplateManagement />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/users" element={
                <ProtectedRoute>
                    <AdminUsers />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/security-logs" element={
                <ProtectedRoute>
                    <SecurityLogs />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/activity-logs" element={
                <ProtectedRoute>
                    <ActivityLogs />
                </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
                <ProtectedRoute>
                    <UserSettings />
                </ProtectedRoute>
            } />
            
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster 
                    position="top-right" 
                    toastOptions={{
                        style: {
                            borderRadius: '0',
                        },
                    }}
                />
                <AppRoutes />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
